import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminNews, formatError } from "../api";
import { Card, PageHeader, Spinner } from "../ui";

export default function NewsList() {
  const [items, setItems] = useState(null);

  const load = () => adminNews.list().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this news post?")) return;
    try { await adminNews.remove(id); toast.success("Deleted."); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  if (!items) return <Spinner />;
  return (
    <div data-testid="admin-news-list">
      <PageHeader
        title="News & Updates"
        description="Publish announcements, position papers and stakeholder updates."
        actions={<Link to="/admin/news/new" className="inline-flex items-center gap-2 rounded-lg bg-[hsl(var(--aoa-royal))] px-4 py-2 text-sm font-semibold text-white hover:bg-[hsl(var(--aoa-navy))]" data-testid="news-new-btn"><Plus className="h-4 w-4" /> New post</Link>}
      />
      {items.length === 0 ? (
        <Card className="p-12 text-center">
          <p className="text-sm text-slate-500">No news posts yet. Create your first one.</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="min-w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <Th>Title</Th><Th>Status</Th><Th>Date</Th><Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((n) => (
                <tr key={n.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <Td>
                    <div className="font-semibold text-[hsl(var(--aoa-navy))]">{n.title}</div>
                    <div className="text-xs text-slate-500">{n.slug}</div>
                  </Td>
                  <Td>
                    <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${n.published ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {n.published ? "Published" : "Draft"}
                    </span>
                  </Td>
                  <Td className="text-xs text-slate-500">{(n.published_at || n.created_at) ? new Date(n.published_at || n.created_at).toLocaleDateString() : "—"}</Td>
                  <Td>
                    <div className="flex gap-2">
                      <Link to={`/admin/news/${n.id}`} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-[hsl(var(--aoa-royal))]"><Pencil className="h-3.5 w-3.5" /> Edit</Link>
                      <button onClick={() => remove(n.id)} data-testid={`news-delete-${n.id}`} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:border-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

const Th = ({ children }) => <th className="text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-600 px-5 py-3">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-5 py-4 text-sm align-middle ${className}`}>{children}</td>;
